'use client'

import React from 'react'
import type { TableProps } from 'antd'
import { Button, Popover, Space, Table, Typography } from 'antd'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import { fetchTrainingTaskList } from '@/service/train'
import Status from '@/app/components/mlops/Status'
import Datetime from '@/app/components/mlops/Datetime'
import ResourceSelectorDesc from '@/app/components/mlops/ResourceSelectorDesc'
import ModelLink from '@/app/components/mlops/ModelLink'
import PermissionTag from '@/app/components/mlops/PermissionTag'
import DatasetLink from '@/app/components/mlops/DatasetLink'
import TrainTypeSelector from '@/app/components/mlops/TrainTypeSelector'
import TruncatedId from '@/app/components/mlops/TruncatedId'

const { Text, Paragraph } = Typography

const TrainingTaskDisplayList: React.FC = () => {
  const { t } = useTranslation()

  const { data, isLoading } = useSWR(
    {
      url: '/api/trains',
      params: {
        page: 1,
        limit: 5, // 只显示5条数据
        search: '',
        is_template: false,
        template_id: '',
        created_from: '',
      },
    },
    fetchTrainingTaskList,
    {
      refreshInterval: 1000,
    },
  )

  const columns: TableProps['columns'] = [
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.idAndName')}</span>,
      dataIndex: 'id',
      width: 220,
      fixed: 'left',
      render: (text, record) => (
        <Space size={0} direction='vertical'>
          <Link
            className='text-[#1677ff] hover:text-[#69b1ff]'
            href={`/train/tasks/${text}`}>
            <Typography.Link>{record.name || '--'}</Typography.Link>
          </Link>
          <TruncatedId id={text} />
        </Space>
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.status')}</span>,
      dataIndex: 'status',
      width: 120,
      render: text => <Status value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.permission')}</span>,
      dataIndex: 'permission',
      width: 100,
      render: text => <PermissionTag value={text} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.model')}</span>,
      dataIndex: 'type',
      width: 120,
      render: text => <TrainTypeSelector value={text} editable={false} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.baseModel')}</span>,
      dataIndex: 'base_model_id',
      width: 200,
      render: (text, record) => <ModelLink model={record.base_model} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.modelName')}</span>,
      dataIndex: 'output_model_obj',
      width: 200,
      render: (text, record) =>
        record.output_model_obj?.id ? (
          <ModelLink model={record.output_model_obj} />
        ) : (
          <Text>{record.output_model || '--'}</Text>
        ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.dataset')}</span>,
      dataIndex: 'dataset_id',
      width: 200,
      render: (_, record) => <DatasetLink dataset={record.dataset} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.resourceInfo')}</span>,
      dataIndex: 'resource_selector',
      width: 200,
      render: (_, record) => (
        <ResourceSelectorDesc
          type='train'
          train_type={record.type}
          resource_selector={record.resource_selector}
        />
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.config')}</span>,
      dataIndex: 'args',
      width: 100,
      render: (text, record) => (
        <Popover
          placement='left'
          content={() => (
            <Paragraph className='w-96 whitespace-pre-line' copyable>
              <Typography>{record.cmd}</Typography>
              {record.args &&
                Array.isArray(record.args) &&
                record.args.map(
                  ({ name, value }: { name: string; value: string }) => (
                    <Typography key={name}>
                      {name} {value}
                    </Typography>
                  ),
                )}
            </Paragraph>
          )}>
          <Button size='small' type='link'>
            {t('train.taskList.view')}
          </Button>
        </Popover>
      ),
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.createdAt')}</span>,
      dataIndex: 'created_at',
      width: 180,
      render: (text, record) => <Datetime value={record.created_at} />,
    },
    {
      title: <span className='whitespace-nowrap'>{t('train.taskList.updatedAt')}</span>,
      dataIndex: 'updated_at',
      width: 180,
      render: (text, record) => <Datetime value={record.updated_at} />,
    },
  ]

  return (
    <Table
      loading={isLoading}
      columns={columns}
      dataSource={data?.data}
      rowKey='id'
      scroll={{ x: 1600 }}
      pagination={false}
    />
  )
}

export default TrainingTaskDisplayList
